import { useEffect, useCallback } from 'react';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  action: () => void;
  description: string;
  category: 'navigation' | 'actions' | 'ui';
}

interface UseKeyboardShortcutsProps {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

/**
 * Hook to register keyboard shortcuts
 * Respects user preferences for keyboard shortcuts
 */
export function useKeyboardShortcuts({ shortcuts, enabled = true }: UseKeyboardShortcutsProps) {
  const { preferences } = useUserPreferences();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user has disabled them
      if (!preferences.keyboardShortcutsEnabled || !enabled) {
        return;
      }

      // Don't trigger shortcuts when typing in input fields
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Check each shortcut
      for (const shortcut of shortcuts) {
        const modifierMatch =
          (shortcut.ctrlKey === undefined || shortcut.ctrlKey === event.ctrlKey) &&
          (shortcut.metaKey === undefined || shortcut.metaKey === event.metaKey) &&
          (shortcut.shiftKey === undefined || shortcut.shiftKey === event.shiftKey) &&
          (shortcut.altKey === undefined || shortcut.altKey === event.altKey);

        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();

        if (keyMatch && modifierMatch) {
          // Check if we need a modifier key
          const needsModifier = 
            shortcut.ctrlKey !== undefined || 
            shortcut.metaKey !== undefined || 
            shortcut.shiftKey !== undefined || 
            shortcut.altKey !== undefined;

          if (needsModifier) {
            event.preventDefault();
            shortcut.action();
            break;
          }
        }
      }
    },
    [shortcuts, preferences.keyboardShortcutsEnabled, enabled]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Get the display string for a keyboard shortcut
 */
export function getShortcutDisplay(shortcut: {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}): string {
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const parts: string[] = [];

  if (shortcut.ctrlKey || shortcut.metaKey) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shiftKey) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (shortcut.altKey) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  // Format key display
  let keyDisplay = shortcut.key;
  if (keyDisplay === ' ') keyDisplay = 'Space';
  if (keyDisplay === 'Escape') keyDisplay = 'Esc';
  if (keyDisplay === '/') keyDisplay = '/';
  if (keyDisplay === ',') keyDisplay = ',';
  
  parts.push(keyDisplay.toUpperCase());

  return parts.join(isMac ? '' : '+');
}

export type { KeyboardShortcut };
